<template>
  <div>
    <transition name="custom-classes-transition">
      <div v-if="isVisible" class="song-select-all-bar" :class="{ 'is-processing': isProcessing }">
        <div class="song-select-all-bar-content">
          <div class="song-select-all-bar-left">
            <button class="song-select-all-bar-close" @click="$emit('close')" :disabled="isProcessing">
              <IconX />
            </button>
            <span class="song-select-all-bar-count">{{ selectedCount }} pitches selected</span>
          </div>

          <div class="song-select-all-bar-separator"></div>

          <div class="song-select-all-bar-actions">
            <button class="song-select-all-bar-action" @click="exportSelected" :disabled="isProcessing">
              <IconList />
              <span>{{ isExporting ? 'Exporting...' : 'Export' }}</span>
            </button>

            <button class="song-select-all-bar-action" @click="openBulkEdit('status')" :disabled="isProcessing">
              <IconCheck />
              <span>Change Status</span>
            </button>

            <button class="song-select-all-bar-action song-select-all-bar-action-danger" @click="deleteSelected" :disabled="isProcessing">
              <IconTrash />
              <span>{{ isDeleting ? 'Deleting...' : 'Delete' }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <PitchBulkEditModal
      :is-visible="showBulkEditModal"
      :field="bulkEditField"
      @save="handleBulkSave"
      @close="closeBulkEditModal"
    />

    <form-modal
      type="export"
      title="Select Columns To Export"
      :model="exportColumnData"
      :isModal="showExportModal"
      :isExporting="isExporting"
      v-on:clear-modal="clearExportModal"
      v-on:save-record="createExport"
    ></form-modal>
  </div>
</template>

<script>
import { IconX, IconTrash, IconList, IconCheck } from '@/Icons/IconBase'
import { PITCH_BULK_DELETE, PITCH_BULK_UPDATE, EXPORT_ALL_COLUMNS, EXPORT_CREATE } from '@/store/actions'
import { PITCH_LOADING } from '@/store/mutations'
import PitchBulkEditModal from '@/components/Modals/PitchBulkEditModal'

export default {
  name: 'PitchSelectAllBar',

  components: {
    IconX,
    IconTrash,
    IconList,
    IconCheck,
    PitchBulkEditModal
  },

  props: {
    selectedCount: {
      type: Number,
      default: 0
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    selectedPitches: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      isDeleting: false,
      isUpdating: false,
      isExporting: false,
      showBulkEditModal: false,
      bulkEditField: null,
      showExportModal: false,
      exportColumnData: {}
    }
  },

  computed: {
    isProcessing() {
      return this.isDeleting || this.isUpdating || this.isExporting
    }
  },

  methods: {
    openBulkEdit(field) {
      this.bulkEditField = field
      this.showBulkEditModal = true
    },

    closeBulkEditModal() {
      this.showBulkEditModal = false
      this.bulkEditField = null
    },

    handleBulkSave({ field, value }) {
      this.closeBulkEditModal()
      this.isUpdating = true
      const pitchIds = this.selectedPitches.map(p => p.id)

      this.$store.dispatch(PITCH_BULK_UPDATE, { pitchIds, field, value })
        .then(() => {
          this.$store.commit('alert', `${pitchIds.length} pitch(es) updated.`)
          this.$emit('clear-selection')
          this.$store.commit(PITCH_LOADING)
          setTimeout(() => {
            this.$emit('refresh-pitches')
            this.isUpdating = false
          }, 1500)
        })
        .catch(err => {
          if (err.response) {
            this.$store.commit('error', err.response.data.message)
          } else {
            this.$store.commit('error', 'Pitches could not be updated.')
          }
          this.isUpdating = false
        })
    },

    exportSelected() {
      this.$store.dispatch(EXPORT_ALL_COLUMNS, 'pitch')
        .then((data) => {
          this.exportColumnData = data
          this.showExportModal = true
        })
        .catch(() => {
          this.$store.commit('error', 'Unable to initiate export.')
        })
    },

    createExport(data) {
      this.isExporting = true
      data.search = { pitch_ids: this.selectedPitches.map(p => p.id) }
      data.scope = 'all'
      data.type = 'pitch'
      this.$store.dispatch(EXPORT_CREATE, data)
        .then(() => {
          this.isExporting = false
          this.clearExportModal()
          this.$store.commit('export', 'Your export is in process.')
        })
        .catch((err) => {
          this.isExporting = false
          this.clearExportModal()
          if (err.response) {
            this.$store.commit('error', err.response.data.message)
          } else {
            this.$store.commit('error', 'Export could not be processed.')
          }
        })
    },

    clearExportModal() {
      this.showExportModal = false
      this.exportColumnData = {}
    },

    deleteSelected() {
      const count = this.selectedPitches.length
      this.$store.dispatch('confirm', `Are you sure you want to delete ${count} pitch(es)? This action cannot be undone.`)
        .then(resp => {
          if (resp === true) {
            this.bulkDeletePitches()
          }
        })
        .catch(err => console.log(err))
    },

    bulkDeletePitches() {
      const pitchIds = this.selectedPitches.map(p => p.id)
      this.isDeleting = true

      this.$store.dispatch(PITCH_BULK_DELETE, pitchIds)
        .then(() => {
          this.$store.commit('alert', `${pitchIds.length} pitch(es) deleted.`)
          this.$emit('close')
        })
        .catch(err => {
          if (err.response) {
            this.$store.commit('error', err.response.data.message)
          } else {
            this.$store.commit('error', 'Pitches could not be deleted.')
          }
        })
        .finally(() => {
          this.isDeleting = false
        })
    }
  }
}
</script>
